if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        
        // Initialize desktop navigation arrows
        this.initializeNavigationArrows();
        
        if (this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        // Scroll the viewer into view (favoring higher position) while adjusting horizontal scroll.
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const rect = activeMedia.getBoundingClientRect();
          const header = document.querySelector('sticky-header');
          const headerHeight = header && header.offsetHeight ? header.offsetHeight : 0;
          const top = rect.top + window.scrollY - headerHeight - 120; // scroll higher
          window.scrollTo({ top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.updateNavigationButtons();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      initializeNavigationArrows() {
        const navigationContainer = this.querySelector('.product-media-navigation');
        if (!navigationContainer) return;

        const prevButton = navigationContainer.querySelector('.product-media-navigation__button--prev');
        const nextButton = navigationContainer.querySelector('.product-media-navigation__button--next');

        if (prevButton) {
          prevButton.addEventListener('click', this.navigatePrevious.bind(this));
        }
        if (nextButton) {
          nextButton.addEventListener('click', this.navigateNext.bind(this));
        }

        // Update button states initially
        this.updateNavigationButtons();
      }

      navigatePrevious() {
        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
        if (!activeMedia) return;

        const prevMedia = activeMedia.previousElementSibling;
        if (prevMedia && prevMedia.classList.contains('product__media-item')) {
          this.setActiveMedia(prevMedia.dataset.mediaId, false);
        } else {
          // Go to last item if at the beginning
          const allMedia = this.elements.viewer.querySelectorAll('.product__media-item');
          const lastMedia = allMedia[allMedia.length - 1];
          if (lastMedia) {
            this.setActiveMedia(lastMedia.dataset.mediaId, false);
          }
        }
        this.updateNavigationButtons();
      }

      navigateNext() {
        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
        if (!activeMedia) return;

        const nextMedia = activeMedia.nextElementSibling;
        if (nextMedia && nextMedia.classList.contains('product__media-item')) {
          this.setActiveMedia(nextMedia.dataset.mediaId, false);
        } else {
          // Go to first item if at the end
          const firstMedia = this.elements.viewer.querySelector('.product__media-item');
          if (firstMedia) {
            this.setActiveMedia(firstMedia.dataset.mediaId, false);
          }
        }
        this.updateNavigationButtons();
      }

      updateNavigationButtons() {
        const navigationContainer = this.querySelector('.product-media-navigation');
        if (!navigationContainer) return;

        const prevButton = navigationContainer.querySelector('.product-media-navigation__button--prev');
        const nextButton = navigationContainer.querySelector('.product-media-navigation__button--next');
        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active');

        if (!activeMedia || !prevButton || !nextButton) return;

        const allMedia = this.elements.viewer.querySelectorAll('.product__media-item');
        const currentIndex = Array.from(allMedia).indexOf(activeMedia);
        const totalMedia = allMedia.length;

        // For now, we'll allow cycling, so never disable buttons
        // If you want to disable at start/end, uncomment these lines:
        // prevButton.disabled = currentIndex === 0;
        // nextButton.disabled = currentIndex === totalMedia - 1;
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
